import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * Fiori Shell Bar
 * - Dark bar (#1B2735) with product logo, app title, and user avatar
 * - Below it sits an Icon Tab Bar that toggles between cross-app pages
 */
const Navigation = ({ email, setEmail, setIsLoggedIn }) => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const userMenuRef = useRef(null);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [userMenuOpen]);

  const handleSignOut = () => {
    setEmail(null);
    localStorage.removeItem("userEmail");
    localStorage.setItem("isLoggedIn", "false");
    setIsLoggedIn(false);
    navigate("/login");
  };

  const initial = email?.[0]?.toUpperCase() || "U";

  return (
    <header className="font-sans">
      {/* Shell Bar */}
      <div className="h-16 bg-fiori-shell text-fiori-text-on-dark shadow-fiori-shell flex items-center px-3 sm:px-4 relative">
        {/* Left: product mark */}
        <div className="flex items-center gap-2 min-w-0">
          <img
            src="/karini-logo.png"
            alt="Karini"
            className="h-8 w-auto object-contain"
          />
          <span className="text-white/60 text-sm hidden sm:inline">on</span>
          <img
            src="/aws-logo.png"
            alt="AWS"
            className="h-8 w-auto object-contain hidden sm:inline-block"
          />
        </div>

        {/* Center: app title (absolutely positioned for true centering) */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <h1 className="text-sm sm:text-[15px] font-semibold tracking-tight text-white truncate pointer-events-auto">
            ERP Exception Intelligent Automation
          </h1>
        </div>

        {/* Right: user actions */}
        <div className="ml-auto flex items-center gap-1">
          {email && (
            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setUserMenuOpen((o) => !o)}
                aria-label="User menu"
                className="flex items-center justify-center w-8 h-8 rounded-full bg-white hover:bg-white/20 text-black text-sm font-semibold transition-colors"
              >
                {initial}
              </button>

              {userMenuOpen && (
                <div
                  className="absolute right-0 mt-2 w-72 bg-white rounded-fiori shadow-fiori-popover border border-fiori-border-light z-50 overflow-hidden"
                  role="menu"
                >
                  <div className="flex items-center px-4 py-3 bg-fiori-page-2/60">
                    <span className="flex items-center justify-center w-10 h-10 rounded-full bg-fiori-brand text-white text-base font-semibold mr-3">
                      {initial}
                    </span>
                    <div className="min-w-0">
                      <div className="text-fiori-text font-medium text-sm truncate">
                        {email}
                      </div>
                      <div className="text-xs text-fiori-text-secondary">
                        Signed in
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="flex items-center w-full px-4 py-2.5 text-sm text-fiori-text hover:bg-fiori-card-hover border-t border-fiori-border-light"
                  >
                    <svg
                      className="w-4 h-4 mr-2 text-fiori-text-secondary"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h4a2 2 0 012 2v1"
                      />
                    </svg>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Icon Tab Bar */}
      <TabNavigation />
    </header>
  );
};

export default Navigation;

function TabNavigation() {
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = [
    { path: "/p2p", label: "P2P Exceptions" },
    { path: "/o2c", label: "Invoice Management" },
    // { path: "/credit-memo", label: "Credit Memo" },
    // { path: "/payment-reconciliation", label: "Payment Reconciliation" },
    { path: "/meter-2-cash", label: "Meter to Cash" },
    { path: "/order-processing", label: "Order Processing" },
    { path: "/credit-management", label: "Credit Management" },
    { path: "/cash-application", label: "Cash Application" },
  ];

  return (
    <nav
      className="bg-white border-b border-fiori-border-light"
      aria-label="Main"
    >
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-4">
        <ul className="flex flex-wrap -mb-px">
          {tabs.map((tab) => {
            const active = location.pathname === tab.path;
            return (
              <li key={tab.path} className="mr-1">
                <button
                  type="button"
                  onClick={() => navigate(tab.path)}
                  className={`relative px-4 py-3 text-base font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-fiori-brand
                    ${
                      active
                        ? "text-fiori-brand"
                        : "text-fiori-text-secondary hover:text-fiori-text"
                    }`}
                  aria-current={active ? "page" : undefined}
                >
                  {tab.label}
                  <span
                    className={`absolute left-2 right-2 -bottom-px h-[3px] rounded-t ${
                      active ? "bg-fiori-brand" : "bg-transparent"
                    }`}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
